import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Search, X } from 'lucide-react'
import { IconButton, Input } from '@/components/ui'
import { useSapBusinessPartners } from '../api/contractsApi.ts'

interface SapPartnerPickerProps {
  label?: ReactNode
  hint?: ReactNode
  placeholder?: string
  /** Aktuell gesetzter SAP CardCode. */
  value: string
  /** Wird mit dem neuen CardCode (oder leerem String) aufgerufen. */
  onChange: (cardCode: string, name?: string) => void
  /** Initiale Anzeige (z. B. wenn Vertrag bereits einen Lieferanten gespeichert hat). */
  initialName?: string
}

/**
 * Picker fuer SAP-Geschaeftspartner. Lieferanten und Kunden in implec sind in
 * SAP fuehrend (siehe CLAUDE.md / Plan), deshalb suchen wir live ueber
 * `/api/sap/partners?search=…` und speichern nur den CardCode plus
 * Snapshot-Namen. Eigene Stamm­datenpflege wuerde nur duplizieren.
 */
export function SapPartnerPicker({
  label,
  hint,
  placeholder = 'SAP-Partner suchen (CardCode oder Name) …',
  value,
  onChange,
  initialName,
}: SapPartnerPickerProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [selectedName, setSelectedName] = useState<string | null>(initialName ?? null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const { data: partners = [], isLoading } = useSapBusinessPartners(query)

  useEffect(() => {
    if (initialName !== undefined) setSelectedName(initialName ?? null)
  }, [initialName])

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  function clearSelection() {
    setSelectedName(null)
    setQuery('')
    onChange('', undefined)
  }

  if (value) {
    return (
      <div className="space-y-1">
        {label ? <p className="text-small font-medium text-gray-700">{label}</p> : null}
        <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
          <span className="text-small font-medium text-gray-900">{selectedName ?? value}</span>
          <span className="text-caption text-gray-500">({value})</span>
          <span className="ml-auto" />
          <IconButton
            icon={<X className="w-3.5 h-3.5" />}
            aria-label="Auswahl entfernen"
            variant="ghost"
            size="sm"
            onClick={clearSelection}
          />
        </div>
        {hint ? <p className="text-caption text-gray-500">{hint}</p> : null}
      </div>
    )
  }

  return (
    <div className="space-y-1" ref={containerRef}>
      <div className="relative">
        <Input
          label={label}
          hint={hint}
          placeholder={placeholder}
          leadingIcon={<Search className="w-4 h-4" />}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.currentTarget.value)
            setOpen(true)
          }}
        />
        {open && query.length >= 2 ? (
          <div className="absolute left-0 right-0 top-full z-popover mt-1 max-h-72 overflow-auto rounded-md border border-gray-200 bg-white shadow-popover">
            {isLoading ? (
              <p className="px-3 py-2 text-caption text-gray-500">Suche …</p>
            ) : partners.length === 0 ? (
              <p className="px-3 py-2 text-caption text-gray-500">Keine Treffer.</p>
            ) : (
              <ul className="py-1">
                {partners.map((p) => (
                  <li key={p.CardCode}>
                    <button
                      type="button"
                      className="flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-small hover:bg-gray-50"
                      onClick={() => {
                        onChange(p.CardCode, p.CardName)
                        setSelectedName(p.CardName)
                        setQuery('')
                        setOpen(false)
                      }}
                    >
                      <span className="font-medium text-gray-900">{p.CardName}</span>
                      <span className="text-caption text-gray-500">{p.CardCode}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}
